import { useRouter } from 'next/router';
import React, { Children, Ref, useRef } from 'react';
import { useEffect, useState } from 'react';

import AdminNavigation from '@/components/admin/navigation';
import AnalystFooter from '@/components/analyst/footer';
import { CurrentOrganizationSelector } from '@/components/global/current-organization-selector';
import Footer from '@/components/global/footer';
import { GlobalNavigationBar, ProductOnlyGlobalNavigationBar } from '@/components/global/global-navigation-bar';
import { useGlobalState } from '@/components/global/global-state';
import MainFooter from '@/components/global/main-footer';
import Meta from '@/components/global/meta';
import SiteHeader from '@/components/global/site-header';
import { StocktwitsAcquisitionAnnouncementBanner } from '@/components/global/stocktwits-acquisition-announcement-banner';
import UserProfileDropdown from '@/components/global/user-profile-dropdown';
import IndexBuilderNav from '@/components/index-builder/navigation';
import { IndexBuilderContextProvider } from '@/components/index-builder/useIndexBuilderContext';
import { usePosthogTracking } from '@/helpers/hooks/usePosthogTracking';
import cn from '@/lib/cn';

type Props = {
    children: React.ReactNode;
    hideNavigation?: boolean;
};

export const EmptyLayout = ({ children }: Props) => {
    return (
        <>
            <Meta />
            <main id="root">{children}</main>
        </>
    );
};

export const StandardLayout = ({ children }: Props) => {
    return (
        <>
            <Meta />
            <StocktwitsAcquisitionAnnouncementBanner />
            <CurrentOrganizationSelector />
            <div className="flex flex-col min-h-screen justify-between">
                <GlobalNavigationBar />

                <main
                    id="root"
                    className="flex flex-col h-full flex-grow"
                >
                    <div>{children}</div>
                </main>
                <Footer />
            </div>
        </>
    );
};

export const AdminLayout = ({ children }: Props) => {
    const router = useRouter();
    const { globalState } = useGlobalState();

    if (globalState.currentUserLoading) {
        return <></>;
    } else if (!globalState.currentUser?.isThematicAdmin) {
        router.push('/');
        return <></>;
    }

    return (
        <>
            <Meta />
            <div className="min-h-screen flex items-stretch">
                <AdminNavigation />
                <main
                    id="root"
                    className="w-full"
                >
                    {children}
                </main>
            </div>
        </>
    );
};

export const IndexBuilderLayout = ({ children }: Props) => {
    const router = useRouter();

    const [rawBuilderIndexId, setRawBuilderIndexId] = useState<string>();

    useEffect(() => {
        if (!router.isReady) return;

        setRawBuilderIndexId(router.query.builderIndexId as string);
    }, [router.isReady, router.query]);

    // Route parameters are not immediately available - https://stackoverflow.com/questions/61040790/userouter-withrouter-receive-undefined-on-query-in-first-render
    // Since this is relevant to a Provider component, not sure how to use `useEffect` + `isReady` as recommended without hiding the entire page/layout.
    if (!rawBuilderIndexId) {
        return <></>;
    }

    return (
        <IndexBuilderContextProvider rawBuilderIndexId={rawBuilderIndexId as string}>
            <>
                <Meta />
                <StocktwitsAcquisitionAnnouncementBanner />
                <div className="flex flex-col h-screen">
                    <CurrentOrganizationSelector />
                    <ProductOnlyGlobalNavigationBar />

                    <div className="flex flex-grow">
                        <IndexBuilderNav />
                        <main
                            id="root"
                            className="w-full overflow-y-auto h-full"
                        >
                            {children}
                        </main>
                    </div>
                </div>
            </>
        </IndexBuilderContextProvider>
    );
};

export const AnalystLayout = ({ children }: Props) => {
    return (
        <>
            <Meta />
            <StocktwitsAcquisitionAnnouncementBanner />
            <div className="flex flex-col min-h-screen">
                <SiteHeader analystLinkOnly={true} />
                <main id="root">{children}</main>
                <AnalystFooter />
            </div>
        </>
    );
};

export interface ChildrenProps {
    intersectionRef: Ref<HTMLDivElement>;
    children: React.ReactNode;
}

export const RedesignLayout = ({ children }: Props) => {
    const { locations } = usePosthogTracking();
    const { globalState } = useGlobalState();
    const { currentUser, currentUserLoading } = globalState;
    const intersectionRef = useRef(null);
    // Need to pass
    const clonedChildren = Children.map(children, (child) =>
        React.cloneElement(child as React.ReactElement<ChildrenProps>, { intersectionRef }, child)
    );

    return (
        <>
            <Meta />
            <div className="overflow-x-hidden min-h-screen flex flex-col">
                <StocktwitsAcquisitionAnnouncementBanner />
                <CurrentOrganizationSelector />

                <div className="sticky top-6 left-0 h-12 w-full my-4 z-2000">
                    <GlobalNavigationBar />

                    {!currentUserLoading && currentUser && (
                        <UserProfileDropdown
                            className={cn(
                                'hidden animate-fadeIn opacity-0 lg:inline-flex lg:opacity-100 absolute top-0 right-8 z-20'
                            )}
                            trackingProperites={{
                                section: locations.GLOBAL_NAV,
                            }}
                        />
                    )}
                </div>

                <main
                    id="root"
                    className="flex-grow"
                >
                    {clonedChildren}
                </main>

                <MainFooter />
            </div>
        </>
    );
};

export const NewLayoutNoCondensedLogo = ({ children }: Props) => {
    const intersectionRef = useRef(null);
    // Need to pass
    const clonedChildren = Children.map(children, (child) =>
        React.cloneElement(child as React.ReactElement<ChildrenProps>, { intersectionRef }, child)
    );

    return (
        <>
            <Meta />
            <div className="pb-24 overflow-x-hidden">
                <StocktwitsAcquisitionAnnouncementBanner />
                <CurrentOrganizationSelector />

                <GlobalNavigationBar />

                <main id="root">{clonedChildren}</main>

                <MainFooter />
            </div>
        </>
    );
};
